import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {MTAApi, type QueueItem} from "components/src/api/admin/generated";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {Truncate} from "components/src/common/Truncate";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {DateUtils} from "components/src/utils/DateUtils";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {
    AtSign as MTAIcon,
    Bin as DeleteIcon,
    MinusCircle as HoldIcon,
    Redo as RequeueIcon,
    Redo as FlushQueueIcon,
    Refresh as ReleaseIcon,
    SendMail as SendMailIcon,
    Xmark as BounceIcon
} from "iconoir-react";
import {useEffect, useState} from 'react'
import {Dropdown} from "react-bootstrap";
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Nav from "react-bootstrap/Nav";
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useNavigate} from "react-router-dom";
import {MTASendMailPopup} from "./MTASendMailPopup";
import {MTAViewEmailDialog} from "./MTAViewEmailDialog";

/*
 * REST api for accessing the back-end
 */
const mtaApi = new MTAApi(ApiConfig.config());

type OnClick = () => void;

interface ColumnProps {
    value?: string
    children?: React.ReactNode
    className? : string
    maxLength? : number
    onClick? : OnClick
}

const Column = ({value, children, className, maxLength = 60, onClick}: ColumnProps) => {
    return (
        <td onClick={onClick} className={"user-select-all cursor-pointer " + className}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

export const MTAQueue = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The mail queue items
     */
    const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
    /**
     * The total number of MTA queue items
     */
    const [queueSize, setQueueSize] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected queue item
     */
    const [selectedQueueItems, setSelectedQueueItems] = useState<QueueItem[]>([]);
    /**
     * The clicked queue item
     */
    const [clickedQueueItem, setClickedQueueItem] = useState<QueueItem>();
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "confirm bounce" modal dialog will be shown
     */
    const [showBounceConfirm, setShowBounceConfirm] = useState<boolean>(false);
    /**
     * If true, the "mail dialog" modal dialog will be shown
     */
    const [showMailDialog, setShowMailDialog] = useState<boolean>(false);
    /**
     * If true, the "send mail" modal dialog will be shown
     */
    const [showSendMailDialog, setShowSendMailDialog] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() =>
    {
        logger.debug("Load queue size")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerGetQueueSize()
            .then(response => {
                setQueueSize(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting queue size", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load queue items")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerGetQueueList((currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                setQueueItems(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting queue list", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    const onChangeSelect = (checked : boolean, queueItem : QueueItem) =>
    {
        setSelectedQueueItems(checked ?
            [...selectedQueueItems, queueItem] :
            selectedQueueItems.filter(q => q.queue_id !== queueItem.queue_id)
        );
    }

    const onClickQueueId = (queueItem : QueueItem) =>
    {
        setClickedQueueItem(queueItem);
        setShowMailDialog(true);
    }

    const invertSelection = () => {
        setSelectedQueueItems([...queueItems].filter(q => !selectedQueueItems.includes(q)));
    }

    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerDeleteMailFromQueue(selectedQueueItems.map(q => q.queue_id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting queue items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedQueueItems([]);
            })
    }

    const bounceSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerBounceMailFromQueue(selectedQueueItems.map(q => q.queue_id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error bouncing queue items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowBounceConfirm(false);
                setSelectedQueueItems([]);
            })
    }

    const holdSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerHoldMailFromQueue(selectedQueueItems.map(q => q.queue_id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error holding queue items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setSelectedQueueItems([]);
            })
    }

    const releaseSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerReleaseMailFromQueue(selectedQueueItems.map(q => q.queue_id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error releasing queue items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setSelectedQueueItems([]);
            })
    }

    const requeueSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerRequeueMailFromQueue(selectedQueueItems.map(q => q.queue_id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error re-queueing items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setSelectedQueueItems([]);
            })
    }

    const flushQueue = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        mtaApi.mTAControllerFlushQueue()
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error flushing queue", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }

    /**
     * Is called when the MTAViewEmailDialog is closed
     */
    const onMTAViewEmailDialogClose = () => {
        setShowMailDialog(false);
    }

    /**
     * Is called when the MTA Send Mail Dalog is closed
     */
    const onMTASendMailDalogClose = () =>
    {
        setShowSendMailDialog(false);

        // always reload
        // the send mail dialog is not auto closed when the send button is clicked
        setForceReload(true);
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("mta-queue.are-you-sure-delete-emails")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("mta-queue.are-you-sure-bounce-emails")}
                           confirmButtonTitle={t("mta-queue.bounce")}
                           onClickConfirmEvent={bounceSelected}
                           showConfirm={showBounceConfirm}
                           setShowConfirm={setShowBounceConfirm}
            />

            {showMailDialog && clickedQueueItem?.queue_id !== undefined && <MTAViewEmailDialog queueID={clickedQueueItem.queue_id} onClose={onMTAViewEmailDialogClose} />}
            {showSendMailDialog && <MTASendMailPopup onClose={onMTASendMailDalogClose} />}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<MTAIcon />} title={t("mta-queue.title")} onClickReload={() => setForceReload(true)}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex flex-fill justify-content-end align-items-center">
                                <ActionsDropdown>
                                    <Dropdown.Item onClick={() => flushQueue()}>
                                        <FlushQueueIcon className="text-success"/> {t("mta-queue.flush-queue")}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setShowSendMailDialog(true)}>
                                        <SendMailIcon className="text-success"/> {t("mta-queue.send-mail")}
                                    </Dropdown.Item>
                                </ActionsDropdown>
                            </div>
                        </div>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x" bordered hover>
                                <thead className="sticky-top">
                                <tr className="text-nowrap">
                                    {selectedQueueItems.length === 0 ?
                                        <>
                                            <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                            <th>{t("mta-queue.queue-name")}</th>
                                            <th>{t("mta-queue.queue-id")}</th>
                                            <th>{t("mta-queue.arrival-time")}</th>
                                            <th>{t("mta-queue.message-size")}</th>
                                            <th>{t("mta-queue.sender")}</th>
                                            <th>{t("mta-queue.recipients")}</th>
                                            <th>{t("mta-queue.forced-expire")}</th>
                                        </>
                                        :
                                        <th colSpan={8}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedQueueItems([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedQueueItems.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                        <DeleteIcon className="text-danger"/> {t("mta-queue.delete-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => setShowBounceConfirm(true)}>
                                                        <BounceIcon className="text-danger"/> {t("mta-queue.bounce-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => holdSelected()}>
                                                        <HoldIcon className="text-success"/> {t("mta-queue.hold-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => releaseSelected()}>
                                                        <ReleaseIcon className="text-success"/> {t("mta-queue.release-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => requeueSelected()}>
                                                        <RequeueIcon className="text-success"/> {t("mta-queue.requeue-selected")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    }
                                </tr>
                                </thead>
                                <tbody>
                                {queueItems?.map((q: QueueItem) => (
                                    <tr key={q.queue_id} className="text-nowrap">
                                        <Column className="td-w-min">
                                            <Form.Check
                                                type="checkbox"
                                                className="me-0"
                                                checked={selectedQueueItems.find((sqi) =>  sqi.queue_id === q.queue_id) !== undefined}
                                                onChange={(e) => onChangeSelect(e.target.checked, q)}
                                                inline
                                            />
                                        </Column>
                                        <Column value={q.queue_name} onClick={() => onClickQueueId(q)} />
                                        <Column value={q.queue_id} onClick={() => onClickQueueId(q)} />
                                        <Column value={DateUtils.fromTimestampToLocaleDateStringShort(q.arrival_time * 1000)} onClick={() => onClickQueueId(q)} />
                                        <Column value={`${q.message_size}`} onClick={() => onClickQueueId(q)} />
                                        <Column value={q.sender} onClick={() => onClickQueueId(q)} />
                                        <Column value={q.recipients.map(r => r.address + "/" + r.delay_reason).join(", ")} onClick={() => onClickQueueId(q)} />
                                        <Column value={`${q.forced_expire}`} onClick={() => onClickQueueId(q)} />
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                    right={<Pager itemCount={queueSize ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>
        </>
    );
}
